import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Mail } from 'lucide-react';
import { faqSections, type FAQItem as FAQItemData } from '@/data/faq';

const FAQItem: React.FC<{ item: FAQItemData; isOpen: boolean; onToggle: () => void }> = ({
  item,
  isOpen,
  onToggle,
}) => {
  return (
    <div className="border-b border-[color-mix(in_srgb,var(--navy)_10%,transparent)] last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full py-4 flex items-center justify-between text-left hover:bg-[color-mix(in_srgb,var(--navy)_5%,transparent)] transition-colors rounded-lg px-2 -mx-2"
      >
        <span className="text-maslow-navy font-medium pr-4">{item.question}</span>
        <ChevronDown
          className={`w-5 h-5 text-maslow-gold flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-[color-mix(in_srgb,var(--navy)_70%,transparent)] pb-4 leading-relaxed pl-2">{item.answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQPage = () => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (sectionIndex: number, itemIndex: number) => {
    const key = `${sectionIndex}-${itemIndex}`;
    setOpenItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="min-h-screen bg-maslow-cream">
      <Helmet>
        <title>FAQ | Maslow NYC, private restrooms in SoHo</title>
        <meta name="description" content="Common questions about Maslow — private restrooms in SoHo, New York City. Opening 2028." />
        <link rel="canonical" href="https://maslow.nyc/faq" />
      </Helmet>

      {/* Header */}
      <div className="bg-maslow-navy py-16">
        <div className="container mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-serif font-bold text-white text-center"
          >
            Frequently Asked Questions
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/70 text-center mt-4 max-w-xl mx-auto"
          >
            Short answers about the rooms, the Hull, booking and paying.
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-8"
        >
          {faqSections.map((section, sectionIndex) => (
            <section key={section.title} className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-serif font-bold text-maslow-navy mb-4 pb-2 border-b border-[color-mix(in_srgb,var(--gold)_30%,transparent)]">
                {section.title}
              </h2>
              <div>
                {section.items.map((item, itemIndex) => (
                  <FAQItem
                    key={itemIndex}
                    item={item}
                    isOpen={openItems[`${sectionIndex}-${itemIndex}`] || false}
                    onToggle={() => toggleItem(sectionIndex, itemIndex)}
                  />
                ))}
              </div>
            </section>
          ))}

          {/* Contact Section */}
          <section className="bg-maslow-navy rounded-xl p-8 text-center">
            <h2 className="text-2xl font-serif font-bold text-white mb-3">
              Still have questions?
            </h2>
            <p className="text-white/70 mb-6">
              Ask. If it's a good question it ends up on this page.
            </p>
            <a
              href="mailto:hello@maslow.nyc?subject=Question"
              className="inline-flex items-center gap-2 bg-maslow-gold text-maslow-navy font-bold px-6 py-3 rounded-lg hover:bg-maslow-gold-hover transition-colors"
            >
              <Mail className="w-5 h-5" />
              hello@maslow.nyc
            </a>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQPage;
